import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAbility } from '@casl/react';
import axios, { AxiosResponse } from 'axios';
import { set } from 'lodash';

import { ROUTES } from '@/router';
import { GoogleAnalytics } from '@/services/Analytics';
import {
  Appcues,
  getBooleanDisplay,
  getDistributionChannel,
  getUserRoleDisplay,
} from '@/services/Appcues';
import { Axios } from '@/services/Axios';
import { BroadcastActions, BroadcastService } from '@/services/Broadcast';
import { BrowserStorageService } from '@/services/BrowserStorage';
import { EnvironmentService } from '@/services/Environment';
import { InactivityMonitoringService } from '@/services/InactivityMonitoring';
import { Sentry } from '@/services/Sentry';
import { GetAvailableCompaniesRequestClientVm } from '@/store/reducers/user/types';
import {
  AuthenticationResponseStatus,
  AvailableCompanyVm,
  CompanyVm,
  CreateAccessTokenForAdminLoginRequestVm,
  CreateAccessTokenResponseVm,
  CustomErrorsMessages,
  CustomExceptions,
  FeatureFlagsVm,
  GetAvailableCompaniesRequestVm,
  GetAvailableCompaniesResponseVm,
  PasswordResetResponseVm,
  RegisterUserAccountResponseVm,
  SendPasswordResetLinResponseVm,
  SendPasswordResetResponseStatus,
  UserInfoVm,
  ValidateUserInvitationResponseStatus,
  ValidateUserInvitationResponseVm,
} from '@/types';
import { cleanFeatureFlags } from '@/utils/clean-feature-flags';
import { clearPhoneNumberFormatting, formatPhoneNumber } from '@/utils/format-phone-number';
import { parseJwtPayload } from '@/utils/parse-jwt-payload';

import updateAbility from '../ability/ability';
import { AbilityContext } from '../ability/Can';

import { GenericObject, LocationState, Props, UserInfoState } from './types';
import { AuthContext } from './useAuth';

const AuthProvider = (props: Props): ReactElement => {
  const [availableCompanies, setAvailableCompanies] = useState<AvailableCompanyVm[] | null>(null);
  const [storedCredentials, setStoredCredentials] = useState<GenericObject | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfoState>();
  const [error, setError] = useState<string>();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isUserInfoStale, setIsUserInfoStale] = useState<boolean>(true);
  const [getProgressStatus, setProgressStatus] = useState<number | string>('none');
  const [requestInProgress, setRequestInProgress] = useState<boolean | undefined>(undefined);
  const isAppcuesInitialized = useRef(false);

  const portalAccessToken =
    BrowserStorageService.portalAccessToken.get() ||
    BrowserStorageService.portalAccessToken.get(true);

  const dashboardUrlRegexp = new RegExp(/\/dashboard\/[0-9]-[0-9A-Za-z]+/);

  const location: LocationState = useLocation();
  const history = useHistory();
  const ability = useAbility(AbilityContext);
  // Clear error state if page has been changed

  useEffect(() => {
    setLoading(false);
    setError(undefined);
    setIsUserInfoStale(true);
    setRequestInProgress(undefined);
  }, [location]);

  // Fetch user info on mount
  useEffect(() => {
    if (
      portalAccessToken &&
      ![ROUTES.Auth.CheckInvitation, ROUTES.System.AdminLogin].includes(location.pathname)
    ) {
      fetchUserInfo();
    }
  }, []);

  useEffect(() => {
    if (userInfo) {
      updateAbility(ability, userInfo);

      if (!isAppcuesInitialized.current) {
        const { firstName, lastName, id, companyInfo, role, flags } = userInfo;

        Appcues.identify(id, {
          channel: getDistributionChannel(companyInfo.distributionAgreementSignedDate),
          companyName: companyInfo.name,
          enviroment: EnvironmentService.environment,
          firstName,
          lastName,
          skuPricingEnabled: getBooleanDisplay(Boolean(flags?.includes('SKU_PRICING'))),
          userRole: getUserRoleDisplay(role),
        });

        isAppcuesInitialized.current = true;
      }
    }
  }, [userInfo]);

  // Set authenticated state depending on access token
  useEffect(() => {
    setIsAuthenticated(Boolean(portalAccessToken));
  }, [portalAccessToken]);

  const tokenLogoutHandler = () => {
    if (
      !BrowserStorageService.portalAccessToken.get() &&
      !BrowserStorageService.portalAccessToken.get(true)
    ) {
      logout();
    }
  };

  const handleResponseErrorStatus = (status: AuthenticationResponseStatus) => {
    const StatusKey = AuthenticationResponseStatus[status];

    setError(CustomErrorsMessages[StatusKey] || CustomErrorsMessages.SomethingWentWrong);
  };

  useEffect(() => {
    if (isAuthenticated) {
      window.addEventListener('storage', tokenLogoutHandler);
    }

    return () => window.removeEventListener('storage', tokenLogoutHandler);
  }, [isAuthenticated]);

  async function reCaptchaHeader(action: string) {
    const xReCaptchaId = BrowserStorageService.xReCaptchaId.get();

    return {
      headers: {
        'x-recaptcha-response':
          xReCaptchaId ??
          (await grecaptcha.execute(EnvironmentService.getGoogleReCaptchaAPIKey(), {
            action,
          })),
      },
    };
  }

  async function fetchUserInfo() {
    try {
      setLoading(true);
      setRequestInProgress(true);
      const response: AxiosResponse<UserInfoVm> = await Axios.get('/auth/userinfo');

      const { companyInfo, id, isAssistedByAdmin, role } = response.data;

      await BroadcastService.postMessage(
        BroadcastActions.checkIfTheSessionIsTheSame(
          BrowserStorageService.portalAccessToken.get(isAssistedByAdmin) as string,
        ),
      );

      if (isAssistedByAdmin) {
        // Turn ON inactivity monitoring
        InactivityMonitoringService.start({
          maxIdleTime: 1000 * 60 * 15, // 15 minutes
          onIdleTimeEndCallback: logout,
        });
      }
      const { data } = response;
      const { assistingAdminId } = parseJwtPayload();

      data.companyInfo.companyContact.companyPhoneNumber = formatPhoneNumber(
        data.companyInfo.companyContact.companyPhoneNumber,
      );

      const companySettingsResponse: AxiosResponse<FeatureFlagsVm> = await Axios.get(
        '/companies/settings',
      );

      set(data, 'companyInfo.settings', companySettingsResponse.data);

      const featureFlagsResponse: AxiosResponse<FeatureFlagsVm> = await Axios.get(
        '/companies/feature-flags',
      );

      const flags = cleanFeatureFlags(featureFlagsResponse.data.flags);

      props.setLegacyUserInfo({ ...data, flags });
      setUserInfo({ ...data, flags });
      setIsUserInfoStale(false);

      GoogleAnalytics.fetchUserInfo(
        id,
        role,
        companyInfo.id,
        companyInfo.name,
        isAssistedByAdmin,
        assistingAdminId,
        companyInfo?.companyContact.companyState,
      );

      Sentry.setUser();
    } catch (err) {
      if (axios.isCancel(err)) {
        return;
      }

      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          clearUserData();
          history.push(ROUTES.Auth.LogIn);

          return;
        }
      }

      props.setGlobalError(new Error(CustomExceptions.BadUserInfo));
    } finally {
      setLoading(false);
      setRequestInProgress(false);
    }
  }

  async function authorizeWithToken() {
    // Save location from user intent
    const savedRoute = location.pathname;

    BrowserStorageService.postAuthRedirect.remove();

    // check if current route was related to auth routes
    const currentRoute = window.location.pathname;
    const authRoute =
      Object.values(ROUTES.System).includes(currentRoute) ||
      currentRoute.includes(ROUTES.Auth.CreateAccount);

    if (authRoute) {
      // Redirect user to the normal flow
      history.push(ROUTES.Private.Dashboard.ActivityList);
    }

    if (dashboardUrlRegexp.test(savedRoute)) {
      // Redirect user to where they were trying to access
      history.push(savedRoute);
    } else if (savedRoute === ROUTES.Auth.SelectCompany) {
      // Redirect user from branch selection
      const companyRedirect = location.state?.pathname ?? ROUTES.Private.Dashboard.ActivityList;

      history.push(companyRedirect);
    } else {
      // Redirect user to the normal flow
      history.push(ROUTES.Private.Dashboard.ActivityList);
    }

    await fetchUserInfo();

    GoogleAnalytics.logIn();
  }

  async function authorizeWithPassword(payload: GetAvailableCompaniesRequestVm) {
    try {
      setLoading(true);
      setStoredCredentials(null);
      const response: AxiosResponse<CreateAccessTokenResponseVm> = await Axios.post(
        '/auth/authorize-via-password',
        {
          ...payload,
          companyId: BrowserStorageService.userCompanyId.get(),
        },
        await reCaptchaHeader('authorize_via_password'),
      );

      if (response.data.status === AuthenticationResponseStatus.Success) {
        BrowserStorageService.portalAccessToken.set(response.data.accessToken);
        setIsAuthenticated(true);

        await authorizeWithToken();
      }
    } catch (err) {
      setLoading(false);

      if (axios.isAxiosError(err)) {
        setError(err.message);
      }
    }
  }

  async function login(credentials: GetAvailableCompaniesRequestClientVm) {
    clearAuthError();
    setLoading(true);

    const payload: GetAvailableCompaniesRequestVm = {
      ...credentials,
      password: credentials.password.split(''),
    };

    if (!availableCompanies?.length) {
      try {
        clearStorages();
        const response: AxiosResponse<GetAvailableCompaniesResponseVm> = await Axios.post(
          '/auth/get-available-companies',
          payload,
          await reCaptchaHeader('get_available_companies'),
        );

        if (
          response.data.status === AuthenticationResponseStatus.InvalidOrExpiredAdminLoginSessionId
        ) {
          history.push(ROUTES.Auth.LogIn);

          return;
        }

        if (response.data.status !== AuthenticationResponseStatus.Success) {
          handleResponseErrorStatus(response.data.status);
          setLoading(false);

          return;
        }
        const { companies } = response.data;

        if (!companies.length) {
          props.setGlobalError(new Error(CustomExceptions.BadUserInfo));
          setError(CustomErrorsMessages.NoCompaniesFound);
          setLoading(false);

          return;
        }

        if (companies.length === 1) {
          BrowserStorageService.userCompanyId.set(companies[0].id);

          if (credentials.adminLoginSessionId) {
            await authorizeWithAdminSession({
              adminLoginSessionId: credentials.adminLoginSessionId,
              companyId: companies[0].id,
            });
          } else {
            await authorizeWithPassword(payload);
          }

          return;
        }
        setAvailableCompanies(companies);
        setStoredCredentials(credentials);
        setLoading(false);

        if (credentials.adminLoginSessionId) {
          history.push(`${ROUTES.Auth.SelectCompany}?session=${credentials.adminLoginSessionId}`);

          return;
        }

        history.push(ROUTES.Auth.SelectCompany, location);

        return;
      } catch (err) {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 400) {
            setError(CustomErrorsMessages.NotRecognizedAccountDetails);
            setLoading(false);

            return;
          }
        }

        setLoading(false);
        history.push(ROUTES.Auth.LogIn);
      }
    }

    await authorizeWithPassword(payload);
  }

  async function sendPasswordResetLink(payload) {
    setLoading(true);
    clearAuthError();

    try {
      const response: AxiosResponse<SendPasswordResetLinResponseVm> = await Axios.post(
        '/auth/send-password-reset-link',
        payload,
        await reCaptchaHeader('send_password_reset_link'),
      );

      if (response.data.status === SendPasswordResetResponseStatus.Success) {
        setRequestInProgress(true);

        return;
      }

      const statusKey = SendPasswordResetResponseStatus[response.data.status];

      setError(CustomErrorsMessages[statusKey] || CustomErrorsMessages.SomethingWentWrong);
      setRequestInProgress(false);
    } catch {
      setError(CustomErrorsMessages.NotRecognizedAccountDetails);
      setRequestInProgress(false);
    } finally {
      setLoading(false);
    }
  }

  async function updateUserInfo(companyInfo: CompanyVm) {
    setRequestInProgress(true);
    try {
      const payload = { ...companyInfo };

      payload.companyContact.companyPhoneNumber = clearPhoneNumberFormatting(
        payload.companyContact.companyPhoneNumber,
      );

      await Axios.put(`/companies/${payload.id}`, payload);
      history.push(ROUTES.Private.Profile.View);
    } catch {
      // Do nothing here for now
    } finally {
      setRequestInProgress(false);
    }
  }

  async function changePassword(values, secret: string) {
    try {
      setProgressStatus('in-progress');
      const payload = {
        secret,
        newPassword: values.newPassword.split(''),
      };

      const response: AxiosResponse<PasswordResetResponseVm> = await Axios.post(
        '/auth/change-password',
        payload,
        await reCaptchaHeader('change_password'),
      );

      if (response.data.isSuccess) {
        setProgressStatus('success');

        return;
      }
      setProgressStatus('none');
    } catch {
      setProgressStatus('none');
    }
  }

  async function validatePasswordResetLink(secret?: string) {
    try {
      setLoading(true);
      setRequestInProgress(undefined);
      const response: AxiosResponse<PasswordResetResponseVm> = await Axios.post(
        '/auth/validate-password-reset-link',
        { secret },
        await reCaptchaHeader('validate_password_reset_link'),
      );

      if (response.data.isSuccess) {
        setRequestInProgress(true);

        return;
      }

      setRequestInProgress(false);
    } catch {
      setRequestInProgress(false);
    } finally {
      setLoading(false);
    }
  }

  function clearStorages(forceClear = false) {
    const xReCaptchaId = BrowserStorageService.xReCaptchaId.get();

    localStorage.clear();
    sessionStorage.clear();

    if (!forceClear) {
      BrowserStorageService.xReCaptchaId.set(xReCaptchaId);
    }
  }

  function clearUserData(forceClear = false) {
    Sentry.clearUser();
    clearStorages(forceClear);
    setAvailableCompanies(null);
    setUserInfo(null);
    setIsAuthenticated(false);
    props.clearLegacyUserInfo();
  }

  function logout() {
    isAppcuesInitialized.current = false;
    clearUserData(true);
    setLoading(false);
    history.push(ROUTES.Auth.Welcome);
  }

  async function authorizeWithAdminSession(payload: CreateAccessTokenForAdminLoginRequestVm) {
    try {
      setLoading(true);
      if (!payload.adminLoginSessionId) {
        history.push(ROUTES.Auth.LogIn);

        return;
      }

      clearStorages();
      await BroadcastService.postMessage(BroadcastActions.dropAllSessions());

      const response: AxiosResponse<CreateAccessTokenResponseVm> = await Axios.post(
        '/auth/authorize-via-admin-login',
        payload,
        await reCaptchaHeader('authorize_via_admin_login'),
      );

      if (response.data.status !== AuthenticationResponseStatus.Success) {
        history.push(ROUTES.Auth.LogIn);

        return;
      }

      BrowserStorageService.portalAccessToken.set(response.data.accessToken, true);

      await authorizeWithToken();
    } catch {
      history.push(ROUTES.Auth.LogIn);
    }
  }

  async function validateInvitationSecret(secret?: string) {
    try {
      setLoading(true);
      clearUserData();

      const response: AxiosResponse<ValidateUserInvitationResponseVm> = await Axios.get(
        `/auth/validate-user-invitation?invitationSecret=${secret}`,
        await reCaptchaHeader('validate_user_invitation'),
      );

      setProgressStatus(response.data.status);

      if (response.data.status === ValidateUserInvitationResponseStatus.Success) {
        setStoredCredentials(response.data);
      }
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function registerUserAccount(payload) {
    try {
      setProgressStatus('in-progress');
      const response: AxiosResponse<RegisterUserAccountResponseVm> = await Axios.post(
        '/auth/register-user-account',
        payload,
        await reCaptchaHeader('register_user_account'),
      );

      if (response.data.isSuccess) {
        GoogleAnalytics.acceptInvitation(response.data.userId ?? '');

        await login({
          email: payload.emailAddress,
          password: payload.password.join(''),
          adminLoginSessionId: null,
        });
        setProgressStatus('success');

        return;
      }

      setProgressStatus('none');
    } catch {
      setProgressStatus('none');
    }
  }

  function clearAuthError() {
    setError(undefined);
  }

  const cached = useMemo(
    () => ({
      authorizeWithAdminSession,
      availableCompanies,
      changePassword,
      clearAuthError,
      error,
      fetchUserInfo,
      isAuthenticated,
      getProgressStatus,
      isUserInfoStale,
      loading,
      login,
      logout,
      registerUserAccount,
      requestInProgress,
      sendPasswordResetLink,
      storedCredentials,
      updateUserInfo,
      userInfo,
      validateInvitationSecret,
      validatePasswordResetLink,
    }),
    [
      availableCompanies,
      error,
      isAuthenticated,
      getProgressStatus,
      isUserInfoStale,
      loading,
      requestInProgress,
      userInfo,
    ],
  );

  return <AuthContext.Provider value={cached}>{props.children}</AuthContext.Provider>;
};

export default AuthProvider;
